/**
 * 随框架发的几套皮肤。
 *
 * 为什么不只发一套默认：**只有一套的机制是没被验证过的机制**。
 * 多两套等于永远有回归测试盯着"切皮肤真的能切、深浅两套都写全了、没有漏掉的槽位"。
 *
 * 每一套都只改了强调色那一组（六个值）。这也是给下游看的示范：
 * **换品牌通常就是这六个值**，不需要重写整套配色。
 */
import { defineSkin, defaultSkin } from './tokens.js';
/**
 * 海：偏冷的青蓝，适合工具类、阅读类。
 *
 * ⚠️ 深色那一组不是把浅色调亮，是另挑的：同一个青蓝在深色底上会显得发灰、读不出来。
 */
export const oceanSkin = defineSkin({
    name: 'ocean',
    label: '海',
    light: {
        accent: '#1d6f8b',
        accentHover: '#186078',
        accentActive: '#145367',
        accentSoft: '#e1eff4',
        accentLine: '#bcd9e4',
        onAccent: '#ffffff',
        shadowAccent: '0 8px 20px -10px rgb(29 111 139 / 45%)',
    },
    dark: {
        accent: '#5fb4d2',
        accentHover: '#77c2dc',
        accentActive: '#4ea1bf',
        accentSoft: '#122a33',
        accentLine: '#1d4453',
        onAccent: '#04161d',
        shadowAccent: '0 8px 20px -10px rgb(95 180 210 / 32%)',
    },
});
/**
 * 林：低饱和的墨绿，适合记录类、健康类。
 */
export const forestSkin = defineSkin({
    name: 'forest',
    label: '林',
    light: {
        accent: '#3f7d55',
        accentHover: '#356c48',
        accentActive: '#2d5c3d',
        accentSoft: '#e6f0e8',
        accentLine: '#c3ddcb',
        onAccent: '#ffffff',
        shadowAccent: '0 8px 20px -10px rgb(63 125 85 / 45%)',
    },
    dark: {
        accent: '#7cc294',
        accentHover: '#8fcfa4',
        accentActive: '#68ad80',
        accentSoft: '#16281c',
        accentLine: '#26412e',
        onAccent: '#06170c',
        shadowAccent: '0 8px 20px -10px rgb(124 194 148 / 32%)',
    },
});
/**
 * 夜：不给浅色留后路的一套——light 和 dark 是同一份深色值。
 *
 * 这类"永远深色"的皮肤（播放器、相机、看图工具）**必须显式把两套写成一样**，
 * 而不是只写 dark 就完事：只写 dark 的话，白天跟随系统时用户拿到的是上一套皮肤的浅色，
 * 而开发时如果一直在深色下工作，这个问题要等到用户报障才会知道。
 */
const nightPalette = {
    // ⚠️ 以**默认深色**为底铺开，不是以浅色。
    // 只写下面这些覆盖项的话，语义色（success/warn/danger/info 及其 soft/line）
    // 会从默认的浅色那套合并进来——那是为米白底调的，落在近黑底上直接读不出来。
    // 这一条对任何"永远深色"的皮肤都成立。
    ...defaultSkin.dark,
    canvas: '#0d0d10',
    surface: '#141419',
    surface2: '#1c1c22',
    surfaceInput: '#191920',
    surfaceSunken: '#08080a',
    overlay: '#202027',
    line: '#2b2b34',
    lineSoft: '#23232b',
    lineStrong: '#3b3b47',
    text: '#eceaf2',
    text2: '#a09eab',
    text3: '#78767f',
    text4: '#56545c',
    textInverse: '#0d0d10',
    accent: '#8b7df0',
    accentHover: '#9d91f3',
    accentActive: '#7a6be5',
    accentSoft: '#1e1a33',
    accentLine: '#332c52',
    onAccent: '#0a0714',
    scrim: 'rgb(0 0 0 / 68%)',
    scrimHeavy: 'rgb(0 0 0 / 88%)',
    tint: 'rgb(255 255 255 / 8%)',
    shadowSm: '0 1px 2px rgb(0 0 0 / 45%)',
    shadowMd: '0 6px 18px -8px rgb(0 0 0 / 65%)',
    shadowLg: '0 16px 40px -16px rgb(0 0 0 / 80%)',
    shadowSheet: '0 -8px 32px -12px rgb(0 0 0 / 65%)',
    shadowAccent: '0 8px 20px -10px rgb(139 125 240 / 35%)',
};
export const nightSkin = defineSkin({
    name: 'night',
    label: '夜',
    light: nightPalette,
    dark: nightPalette,
});
/** 随框架发的全部皮肤。第一套是默认。 */
export const builtinSkins = [defaultSkin, oceanSkin, forestSkin, nightSkin];
//# sourceMappingURL=presets.js.map